import { Component, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  private static RELOAD_KEY = 'eb_chunk_reload';

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);

    // Auto-reload once when a lazy-loaded chunk fails to load (stale deploy)
    const msg = error?.message || '';
    if (
      (msg.includes('Failed to fetch dynamically imported module') ||
       msg.includes('Loading chunk') ||
       msg.includes('Loading CSS chunk')) &&
      !sessionStorage.getItem(ErrorBoundary.RELOAD_KEY)
    ) {
      sessionStorage.setItem(ErrorBoundary.RELOAD_KEY, '1');
      window.location.reload();
      return;
    }
    // Clear the flag so a future deploy can trigger reload again
    sessionStorage.removeItem(ErrorBoundary.RELOAD_KEY);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="text-6xl">😵</div>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            {i18n.t('errors.somethingWentWrong')}
          </h1>
          <p className="max-w-md text-sm text-muted-foreground">
            {i18n.t('errors.unexpectedError')}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={this.handleReset}>
              {i18n.t('errors.tryAgain')}
            </Button>
            <Button onClick={this.handleHome}>
              {i18n.t('errors.goHome')}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
